<script setup lang="ts">
import { ref, computed } from "vue";
import { bemBuilder } from "@chatfood/core-utils";
import { AtomText, SatElasticPanel, useDate } from "@chatfood/design-system";
import { OrderingDetails } from "../ordering-details";
import { LoyaltyRewards } from "../loyalty-rewards";
import {
  ETA_PREPARATION_MARGIN,
  useBasketStore,
  useBusinessStore,
  useCustomerStore,
  useLoyaltyProgramStore,
  useOutletStore,
} from "#imports";
import { useDomainLabel } from "~/composables/use-domain-label";
import { useLocale } from "~/composables/use-locale";

const css = bemBuilder("basket-nav");

const outletStore = useOutletStore();
const basketStore = useBasketStore();
const businessStore = useBusinessStore();
const customerStore = useCustomerStore();
const loyaltyProgram = useLoyaltyProgramStore();

const { t } = useLocale();
const { orderingModeLabel } = useDomainLabel();
const { formatTime, getReadableDate } = useDate();

const showOrderingDetails = ref(false);
const toggleShowMenu = () => {
  showOrderingDetails.value = !showOrderingDetails.value;
};

const location = computed(() => {
  if (basketStore.isForPickup) {
    return outletStore.name;
  }

  if (basketStore.currentLocation) {
    return t("components.basket_nav.current_location");
  }

  if (basketStore.addressId) {
    return (
      customerStore.addresses.find(
        (address) => address.id === basketStore.addressId,
      )?.nickname || t("components.basket_nav.invalid_location")
    );
  }

  return t("components.basket_nav.select_location");
});

const adjustedEta = computed(() => {
  const eta = basketStore.isForDelivery
    ? outletStore.orderingMode.estimatedCompletionMinutes
    : outletStore.orderingMode.preparationMinutes;

  if (eta === 0) return;

  return eta + ETA_PREPARATION_MARGIN;
});

const estimatedCompletion = computed(() => {
  if (basketStore.scheduledTime) {
    return t("components.basket_nav.scheduled_at", {
      day: getReadableDate(basketStore.scheduledTime.day, "ll"),
      time: formatTime(basketStore.scheduledTime.time),
    });
  }

  if (adjustedEta.value) {
    return t("components.basket_nav.estimated_minutes", {
      minutes: adjustedEta.value,
    });
  }

  return "";
});

const eventName = computed(() => {
  if (!businessStore.isCatering) return;

  return (
    basketStore.eventName ?? t("components.basket_nav.event_name_placeholder")
  );
});

const guestsAmount = computed(() => {
  if (!businessStore.isCatering) return;

  return t("components.basket_nav.guests_amount", {
    count: basketStore.pax ?? 0,
  });
});

const displayLoyaltyRewards = computed(() => {
  return (
    loyaltyProgram.isEnabled &&
    loyaltyProgram.service === "deliverect" &&
    loyaltyProgram.canRedeemPoints &&
    customerStore.isLogged
  );
});
</script>

<template>
  <div :class="css()">
    <a
      href=""
      :class="css('list')"
      data-test="open-ordering-details"
      @click.prevent="toggleShowMenu"
    >
      <span data-test="ordering-mode" :class="css('list-item')">
        <AtomText>{{ orderingModeLabel(basketStore.orderingMode) }}</AtomText>
      </span>
      <span
        data-test="location"
        :class="[css('list-item'), css('list-item', 'truncate')]"
      >
        <AtomText>{{ location }}</AtomText>
      </span>
      <span
        v-if="estimatedCompletion"
        data-test="estimated-completion"
        :class="css('list-item')"
      >
        <AtomText>{{ estimatedCompletion }}</AtomText>
      </span>
      <span
        v-if="eventName"
        data-test="event-name"
        :class="[
          css('list-item'),
          css('list-item', 'truncate'),
          css('list-item', 'hide-on-mobile'),
        ]"
      >
        <AtomText>{{ eventName }}</AtomText>
      </span>
      <span
        v-if="guestsAmount"
        data-test="pax"
        :class="[css('list-item'), css('list-item', 'hide-on-mobile')]"
      >
        <AtomText>{{ guestsAmount }}</AtomText>
      </span>
    </a>
    <LoyaltyRewards v-if="displayLoyaltyRewards" />
    <SatElasticPanel
      v-model="showOrderingDetails"
      padding="none"
      width-fit-content
    >
      <OrderingDetails
        :show-menu="showOrderingDetails"
        :class="css('ordering-details')"
        @toggle-show-menu="toggleShowMenu"
      />
    </SatElasticPanel>
  </div>
</template>

<style lang="scss" scoped>
.basket-nav {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);

  &__list {
    max-width: 100%;
    display: flex;
    list-style: none;
    align-items: center;
    margin: 0;
    padding: var(--spacing-sm) 0;
    border-radius: var(--border-radius-7);
    border: solid 1px var(--color-base-70);
    background-color: var(--color-base-100);

    &-item {
      position: relative;
      white-space: nowrap;
      padding-inline: var(--spacing-md);

      &::after {
        content: "";
        position: absolute;
        top: 0;
        right: -1px;
        width: 1px;
        height: 100%;
        background-color: var(--color-base-70);
      }

      &--truncate > * {
        word-break: break-all;
        white-space: normal;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 1;
        line-clamp: 1;
        -webkit-box-orient: vertical;
      }

      &--hide-on-mobile {
        @media (width <= $breakpoint-sm) {
          display: none;
        }
      }
    }
  }

  &__ordering-details {
    @media (width >= $breakpoint-sm) {
      width: 500px;
      border-radius: var(--radii-card-md);
    }
  }
}
</style>
